/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { ToastContainer } from 'react-toastify'
import Modal from 'react-bootstrap/Modal'
import { fetchData, formatPriceWithCommas, RANDOM_COLORS, sanitizeNumericInput, toasterrormsg, toastsuccessmsg, useMenuIdPermissions } from '../../../reusable'
import { PageTitle } from '../../../_metronic/layout/core'
import { KTIcon } from '../../../_metronic/helpers'
import { Tr } from '../../../_metronic/i18n/Tr'

import { Category, AdSetting, TargetMaster } from '../types/adsSettings'
import { NoDataFound } from '../components/NoDataFound'

const buildInitialValues = (data: AdSetting[]) => {
  const vals: Record<string, string> = {}
  data.forEach((item) => {
    vals[`amount_${item.adssettingId}`] = item.amountPerDay != null ? String(item.amountPerDay) : ''
    vals[`maxAds_${item.adssettingId}`] = item.maxAds != null ? String(item.maxAds) : ''
  })
  return vals
}

const buildValidationSchema = (data: AdSetting[]) => {
  const shape: Record<string, any> = {}
  data.forEach((item) => {
    shape[`amount_${item.adssettingId}`] = Yup.number()
      .typeError('Must Be A Number')
      .min(1, 'Must Be At Least 1')
      .max(99999999, 'Maximum 99,999,999 Allowed')
      .required('Amount Is Required')
    shape[`maxAds_${item.adssettingId}`] = Yup.number()
      .typeError('Must Be A Whole Number')
      .integer('Must Be A Whole Number')
      .min(1, 'At Least 1 Required')
      .max(50, 'Max 50 Are Allowed')
      .required('Max Ads Is Required')
  })
  return Yup.object().shape(shape)
}

const ViewBannerSettings = () => {
  const t = Tr()
  const { menuIdEdit } = useMenuIdPermissions()

  const [adSettings, setAdSettings] = useState<AdSetting[]>([])
  const [categoryList, setCategoryList] = useState<Category[]>([])
  const [categoryMap, setCategoryMap] = useState<Record<number, string>>({})
  const [dataLoading, setDataLoading] = useState(true)
  const [saveLoading, setSaveLoading] = useState(false)

  const [targetingOptions, setTargetingOptions] = useState<TargetMaster[]>([])
  const [targetSaveLoading, setTargetSaveLoading] = useState(false)
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [selectedSettingId, setSelectedSettingId] = useState<number | null>(null)
  const [tempCategoryIds, setTempCategoryIds] = useState<number[]>([])
  const [categorySaveLoading, setCategorySaveLoading] = useState(false)
  const tmp = sessionStorage.getItem('admin')
  const adminId = tmp ? JSON.parse(tmp).adminId : ''


  const categoryListApiCall = async () => {
    try {
      const res = await fetchData('admin/categorylist', {})
      if (res.status == '200') {
        setCategoryList(res.data ?? [])
        const map: Record<number, string> = {}
        ;(res.data ?? []).forEach((cat: Category) => {
          map[cat.categoryId] = cat.en_name
        })
        setCategoryMap(map)
      } else {
        toasterrormsg(res?.message || 'An error occurred')
      }
    } catch (error) {
      console.error('Error Fetching category list:', error)
    }
  }

  const adsSettingListApiCall = async () => {
    try {
      const res = await fetchData('admin/ads/setting/list', {})
      if (res.status == '200') {
        const data = res?.data?.list ?? []
        console.log(data);

        setAdSettings(data)
      } else {
        toasterrormsg(res?.message || 'An error occurred')
      }
    } catch (error) {
      console.error('Error Fetching ad details:', error)
    } finally {
      setDataLoading(false)
    }
  }

  const targetMasterListApiCall = async () => {
    try {
      const res = await fetchData('admin/ads/targetmaster/list', {})
      if (res.status == '200') {
        setTargetingOptions(res?.data?.list ?? [])
      } else {
        toasterrormsg(res?.message || 'An error occurred')
      }
    } catch (error) {
      console.error('Error Fetching target master list:', error)
    }
  }

  const saveTargetingSettings = async () => {
    setTargetSaveLoading(true)
    const param = {
      adminId,
      data: targetingOptions.map((opt) => ({
        adstargetmasterId: opt.adstargetmasterId,
        label: opt.label,
        key: opt.key,
        inputType: opt.inputType,
        description: opt.description,
        isActive: opt.isActive,
      })),
    }
    try {
      const res = await fetchData('admin/ads/targetmaster/save', param)
      if (res.status == '200') {
        toastsuccessmsg(res.message)
        targetMasterListApiCall()
      } else {
        toasterrormsg(res?.message || 'An error occurred')
      }
    } catch (error) {
      console.error('Error saving targeting settings:', error)
      toasterrormsg('Failed To Save Targeting Settings')
    } finally {
      setTargetSaveLoading(false)
    }
  }

  useEffect(() => {
    document.title = 'Insayr | Banner Settings'
    categoryListApiCall()
    adsSettingListApiCall()
    targetMasterListApiCall()
  }, [])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: buildInitialValues(adSettings),
    validationSchema: buildValidationSchema(adSettings),
    onSubmit: async (values) => {
      setSaveLoading(true)
      const param = {
        adminId,
        data: adSettings.map((item) => ({
          adssettingId: item.adssettingId,
          amountPerDay: values[`amount_${item.adssettingId}`],
          maxAds: values[`maxAds_${item.adssettingId}`],
          // iconName: item.iconName,
          // placementName: item.placementName,
          // description: item.description,
          // status: 'ENABLE',
        })),
      }

      try {
        const res = await fetchData('admin/ads/setting/save', param)
        if (res.status == '200') {
          toastsuccessmsg(res.message)
          adsSettingListApiCall()
        } else {
          toasterrormsg(res?.message || 'An error occurred')
        }
      } catch (error) {
        console.error('Error updating ad settings:', error)
        toasterrormsg('Failed To Update Settings')
      } finally {
        setSaveLoading(false)
      }
    },
  })


  const savePlacementAvailableCategory = async () => {

    setCategorySaveLoading(true)

    try {
      const res = await fetchData('admin/ads/setting/visibility', {
        adminId,
        adssettingId: selectedSettingId,
        categoryId: tempCategoryIds,
      })
      if (res.status == '200') {
        toastsuccessmsg(res.message)
        setAdSettings((prev) =>
          prev.map((setting) =>
            setting.adssettingId === selectedSettingId
              ? { ...setting, categoryId: tempCategoryIds }
              : setting
          )
        )
        setShowCategoryModal(false)
      } else {
        toasterrormsg(res?.message || 'An error occurred')
      }
    } catch (error) {
      console.error('Error updating category visibility:', error)
      toasterrormsg('Failed To Update Category Visibility')
    } finally {
      setCategorySaveLoading(false)
    }

  }
  const getStableColor = (id: number) => {
    return RANDOM_COLORS[id % RANDOM_COLORS.length]
  }

  if (dataLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
        <span className='spinner-border text-primary' style={{ width: 40, height: 40 }} />
      </div>
    )
  }


  return (
    <>
      <ToastContainer />
      <PageTitle breadcrumbs={[]}>{t('Banner Settings')}</PageTitle>

      <div className='row justify-content-center'>
        <div className='col-12 mb-4'>
          <div className='card p-5'>
            <div className='card-header'>
              <div className='card-title m-0'>
                <h3 className='fw-bolder m-0'>{t('Banner Placement Settings')}</h3>
              </div>
            </div>

            <div className='card-body p-9'>
              {/* ── Info Tips ── */}
              <div className='ads-info-tips'>
                <div className='ads-info-tip ads-info-tip--warning'>
                  <div className='ads-info-tip__icon'>
                    <KTIcon iconName='information-5' className='fs-3 text-warning' />
                  </div>
                  <p className='ads-info-tip__text'>
                    <strong>{t('"Max Ads At A Time"')}</strong>{' '}
                    {t('refers to how many unique ads (from different vendors) can run simultaneously in a placement slot — not the number of banner images.')}
                  </p>
                </div>

                <div className='ads-info-tip ads-info-tip--info'>
                  <div className='ads-info-tip__icon'>
                    <KTIcon iconName='category' className='fs-3 text-primary' />
                  </div>
                  <p className='ads-info-tip__text'>
                    <strong>{t('"Manage Categories"')}</strong>{' '}
                    {t('controls which vendor categories are eligible to advertise in this placement slot. Only vendors belonging to the selected categories can run ads here.')}
                  </p>
                </div>
              </div>

              {/* ── Table ── */}
              <form onSubmit={formik.handleSubmit} noValidate>
                <div className='ads-table-wrapper'>
                  <table className='ads-table'>
                    <thead>
                      <tr>
                        <th className='ads-col-placement'>{t('Placement')}</th>
                        <th className='ads-col-amount'>{t('Amount Per Day (UZS)')}</th>
                        <th className='ads-col-maxads'>{t('Max Ads At A Time')}</th>
                        <th className='ads-col-categories'>{t('Categories')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adSettings && adSettings.map((item) => {
                        const amtKey = `amount_${item.adssettingId}`
                        const maxKey = `maxAds_${item.adssettingId}`
                        const color = getStableColor(item?.adssettingId ?? 0)
                        return (
                          <tr key={item.adssettingId}>
                            {/* Placement info */}
                            <td>
                              <div className='ads-row-placement'>
                                <div className={`ads-row-icon ${color?.bg ?? ''}`}>              <KTIcon
                                  iconName={item?.iconName || 'abstract-26'}
                                  className={`fs-3 ${color?.text ?? ''}`}
                                />
                                </div>
                                <div>
                                  <div className='ads-row-title'>{item?.placementName ? t(item.placementName) : '-'}</div>
                                  <div className='ads-row-desc'>{item?.description ? t(item.description) : '-'}</div>
                                </div>
                              </div>
                            </td>

                            {/* Amount Per Day */}
                            <td>
                              <div className='ads-input-group'>
                                <input
                                  type='text'
                                  name={amtKey}
                                  value={formatPriceWithCommas(formik.values[amtKey])}
                                  onChange={(e) => {
                                    const raw = sanitizeNumericInput(e.target.value)
                                    formik.setFieldValue(amtKey, raw)
                                  }}
                                  onBlur={formik.handleBlur}
                                  placeholder='e.g. 142,857'
                                  className={clsx('form-control', {
                                    'is-invalid':
                                      formik.touched[amtKey] &&
                                      formik.errors[amtKey],
                                  })}
                                  disabled={!menuIdEdit}
                                />
                                <span className='ads-input-suffix'>UZS</span>
                              </div>
                              {formik.touched[amtKey] &&
                                formik.errors[amtKey] && (
                                  <div className='fv-plugins-message-container'>
                                    <span role='alert' className='text-danger'>
                                      {formik.errors[amtKey] as string}
                                    </span>
                                  </div>
                                )}
                            </td>

                            {/* Max Ads At A Time */}
                            <td>
                              <input
                                type='text'
                                name={maxKey}
                                value={formik.values[maxKey] ?? ''}
                                onChange={(e) => {
                                  const raw = sanitizeNumericInput(e.target.value)
                                  formik.setFieldValue(maxKey, raw)
                                }}
                                onBlur={formik.handleBlur}
                                placeholder='e.g. 2'
                                className={clsx('form-control', {
                                  'is-invalid':
                                    formik.touched[maxKey] &&
                                    formik.errors[maxKey],
                                })}
                                disabled={!menuIdEdit}
                              />
                              {formik.touched[maxKey] &&
                                formik.errors[maxKey] && (
                                  <div className='fv-plugins-message-container'>
                                    <span role='alert' className='text-danger'>
                                      {formik.errors[maxKey] as string}
                                    </span>
                                  </div>
                                )}
                            </td>

                            {/* Categories */}
                            <td>
                              {item.adssettingId != 1 && (item.placementName ?? '').toLocaleLowerCase() != 'homepage – top banner' ? (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-primary d-flex align-items-center gap-2'
                                  onClick={() => {
                                    setSelectedSettingId(item.adssettingId)
                                    setTempCategoryIds(item.categoryId || [])
                                    setShowCategoryModal(true)
                                  }}
                                >
                                  <KTIcon iconName='category' className='fs-4' />
                                  {t('Manage')} ({(item.categoryId || []).length})
                                </button>
                              ) : (
                                <span className='ads-category-label text-center'>{t('All categories')}</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                      {(!adSettings || adSettings.length === 0) && (
                        <NoDataFound message='No placement settings found' icon='setting-2' colSpan={4} variant='row' />
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ── Save ── */}
                {menuIdEdit ? (
                  <div className='ads-save-bar'>
                    <button type='submit' disabled={saveLoading} className='btn btn-success'>
                      {!saveLoading && (
                        <span className='d-flex align-items-center gap-2'>
                          <KTIcon iconName='save-2' className='fs-3' />
                          {t('Save Settings')}
                        </span>
                      )}
                      {saveLoading && (
                        <span className='indicator-progress d-flex align-items-center'>
                          {t('Please wait...')}
                          <span className='spinner-border spinner-border-sm align-middle ms-2' />
                        </span>
                      )}
                    </button>
                  </div>
                ) : (
                  ''
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* ── Targeting Settings Card ── */}
      <div className='row justify-content-center'>
        <div className='col-12 mb-4'>
          <div className='card p-5'>
            <div className='card-header'>
              <div className='card-title m-0'>
                <h3 className='fw-bolder m-0'>{t('Audience Targeting Options')}</h3>
              </div>
            </div>

            <div className='card-body p-9'>
              {/* ── Note ── */}
              <div className='ads-note'>
                <KTIcon iconName='information-5' className='fs-3 text-warning mt-1' />
                <p>
                  {t(
                    'These are the master targeting options available across all advertisements. When enabled, vendors will be able to select these targeting criteria while creating their ads. Disabling an option will hide it from the ad creation form for all vendors.'
                  )}
                </p>
              </div>

              {/* ── Table ── */}
              <div className='ads-table-wrapper'>
                <table className='ads-table'>
                  <thead>
                    <tr>
                      <th className='ads-col-placement'>{t('Targeting Criteria')}</th>
                      <th className='ads-col-categories'>{t('Status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {targetingOptions.map((opt) => {
                      const color = getStableColor(opt?.adstargetmasterId ?? 0)

                      return (
                          <tr key={opt.key}>
                            <td>
                              <div className='ads-row-placement'>
                                <div className={`ads-row-icon ${color?.bg ?? ''}`}>                                  <KTIcon
                                  iconName={opt.iconName}
                                  className={`fs-3 ${color?.text ?? ''}`}
                                />
                                </div>
                                <div>
                                  <div className='ads-row-title'>{t(opt.label)}</div>
                                  <div className='ads-row-desc'>{t(opt.description)}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className='form-check form-switch form-check-custom form-check-solid'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={opt.isActive}
                                  onChange={() => {
                                    setTargetingOptions((prev) =>
                                      prev.map((item) =>
                                        item.adstargetmasterId === opt.adstargetmasterId
                                          ? { ...item, isActive: !item.isActive }
                                          : item
                                      )
                                    )
                                  }}
                                  disabled={!menuIdEdit}
                                />
                                <label className='form-check-label fw-semibold text-muted ms-3'>
                                  {opt.isActive ? t('Enabled') : t('Disabled')}
                                </label>
                              </div>
                            </td>
                          </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Save ── */}
              {menuIdEdit ? (
                <div className='ads-save-bar'>
                  <button
                    type='button'
                    disabled={targetSaveLoading}
                    className='btn btn-success'
                    onClick={() => saveTargetingSettings()}
                  >
                    {!targetSaveLoading && (
                      <span className='d-flex align-items-center gap-2'>
                        <KTIcon iconName='save-2' className='fs-3' />
                        {t('Save Targeting Settings')}
                      </span>
                    )}
                    {targetSaveLoading && (
                      <span className='indicator-progress d-flex align-items-center'>
                        {t('Please wait...')}
                        <span className='spinner-border spinner-border-sm align-middle ms-2' />
                      </span>
                    )}
                  </button>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Category Modal ── */}
      {showCategoryModal && (
        <Modal
          show={showCategoryModal}
          onHide={() => setShowCategoryModal(false)}
          centered
        >
          <Modal.Header className='d-flex justify-content-between align-items-center'>
            <Modal.Title className='fw-bolder'>
              {t('Manage Categories')}
            </Modal.Title>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-active-light-primary'
              onClick={() => setShowCategoryModal(false)}
            >
              <KTIcon iconName='cross' className='fs-1' />
            </button>
          </Modal.Header>

          <Modal.Body>
            <div className='d-flex flex-column gap-4'>
              {categoryList && categoryList.map((cat) => {

                const isEnabled = tempCategoryIds.map(Number).includes(Number(cat.categoryId));

                return (
                  <div key={cat.categoryId} className='d-flex align-items-center justify-content-between'>
                    <span className='fw-semibold fs-6'>{cat.en_name}</span>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={isEnabled}
                        onChange={() => {
                          setTempCategoryIds((prev) =>
                            isEnabled
                              ? prev.filter((id) => Number(id) !== Number(cat.categoryId))
                              : [...prev, Number(cat.categoryId)]  // always store as number
                          )
                        }}
                        disabled={!menuIdEdit}
                      />
                      <label className='form-check-label fw-semibold text-muted ms-2'>
                        {isEnabled ? t('Enabled') : t('Disabled')}
                      </label>
                    </div>
                  </div>
                )
              })}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button
              type='button'
              className='btn btn-light'
              onClick={() => setShowCategoryModal(false)}
            >
              {t('Cancel')}
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={async () => {
                savePlacementAvailableCategory()
              }}
              disabled={!menuIdEdit || categorySaveLoading}
            >
              {!categorySaveLoading && (
                <span className='d-flex align-items-center gap-2'>
                  <KTIcon iconName='save-2' className='fs-3' />
                  {t('Save')}
                </span>
              )}
              {categorySaveLoading && (
                <span className='indicator-progress d-flex align-items-center'>
                  {t('Please wait...')}
                  <span className='spinner-border spinner-border-sm align-middle ms-2' />
                </span>
              )}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

export { ViewBannerSettings }
